import { Button, Menu, Text, Loader, Stack, Tooltip, Progress } from "@mantine/core";
import { showNotification, updateNotification } from "@mantine/notifications";
import { IconFileDownload, IconCheck, IconX, IconChevronDown, IconFileOff } from "@tabler/icons-react";
import { useState, useCallback, useId, useMemo } from "react";
import useUserStore from "@/modules/users/store";
import { useReportGenerator } from "../hooks/useReportGenerator";
import { useTemplatesManager } from "../hooks/useTemplatesManager";
import type { ReportData } from "../types";
import type { TemplateListItem } from "../types/template";

const NOTIFICATION_DELAY_SUCCESS = 3000;
const NOTIFICATION_DELAY_ERROR = 5000;

interface ReportTemplatePickerProperties {
  data: ReportData | null;
  disabled?: boolean;
  variant?: "filled" | "outline" | "light" | "subtle";
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
  saveHistory?: boolean;
  saveToStorage?: boolean;
}

/**
 * Returns the appropriate icon based on the current generation status.
 * @param status - The current generation status
 */
function getButtonIcon(status: "idle" | "loading" | "success" | "error") {
  if (status === "success") return <IconCheck size={16} />;
  if (status === "error") return <IconX size={16} />;
  return <IconFileDownload size={16} />;
}

/**
 * Returns the tooltip label based on data availability, error state, and template availability.
 * @param data - The report data
 * @param error - The current error state
 * @param hasTemplates - Whether templates are available
 */
function getTooltipLabel(data: ReportData | null, error: Error | null, hasTemplates: boolean): string {
  if (!data) return "Aguarde o carregamento dos dados";
  if (error) return error.message;
  if (hasTemplates) return "Selecione um template para gerar o relatório";
  return "Nenhum template disponível";
}

/** Properties for the TemplateMenuItem component. */
interface TemplateMenuItemProperties {
  template: TemplateListItem;
  onSelect: (template: TemplateListItem) => Promise<void>;
  disabled: boolean;
}

/**
 * Renders a single template option in the dropdown menu with a stable click handler.
 * @param root0
 * @param root0.template - The template data to display
 * @param root0.onSelect - Callback when the template is selected
 * @param root0.disabled - Whether the menu item is disabled
 */
function TemplateMenuItem({ template, onSelect, disabled }: TemplateMenuItemProperties) {
  const handleClick = useCallback(() => {
    void onSelect(template);
  }, [onSelect, template]);

  return (
    <Menu.Item onClick={handleClick} disabled={disabled}>
      <Stack gap={2}>
        <Text size="sm" fw={500}>
          {template.nome}
        </Text>
        {template.descricao && (
          <Text size="xs" c="dimmed" lineClamp={2}>
            {template.descricao}
          </Text>
        )}
      </Stack>
    </Menu.Item>
  );
}

/**
 * Component that combines template selection with report generation.
 *
 * Features:
 * - Lists available templates from Firestore
 * - Shows template description on hover
 * - Generates PDF on template selection
 * - Loading state with progress indicator
 * - Success/error notifications
 * @param root0
 * @param root0.data
 * @param root0.disabled
 * @param root0.variant
 * @param root0.size
 * @param root0.fullWidth
 * @param root0.saveHistory
 * @param root0.saveToStorage
 */
export function ReportTemplatePicker({
  data,
  disabled = false,
  variant = "filled",
  size = "sm",
  fullWidth = false,
  saveHistory = false,
  saveToStorage = false
}: ReportTemplatePickerProperties) {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const notificationId = useId();

  // Get empresa role to filter templates
  const activeEmpresa = useUserStore((store) => store.activeEmpresa);
  const isConvidada = activeEmpresa?.role === "CONVIDADA";

  // Get available templates
  const { templates: allTemplates, isLoading: isLoadingTemplates } = useTemplatesManager();

  // Filter templates based on empresa role
  // - CONVIDADA: only active templates with acessivelConvidada=true
  // - Others: all active templates
  const templates = useMemo(() => {
    return allTemplates.filter((template) => {
      if (!template.ativo) return false;
      if (isConvidada) return template.acessivelConvidada === true;
      return true;
    });
  }, [allTemplates, isConvidada]);

  const { generatePDF, isGenerating, progress, error } = useReportGenerator({
    autoDownload: true,
    saveHistory,
    saveToStorage,
    onSuccess: () => {
      setStatus("success");
      updateNotification({
        id: notificationId,
        title: "Relatório gerado",
        message: "O download do seu relatório foi iniciado",
        color: "green",
        icon: <IconCheck size={16} />,
        autoClose: NOTIFICATION_DELAY_SUCCESS,
        loading: false
      });
      setTimeout(() => setStatus("idle"), NOTIFICATION_DELAY_SUCCESS);
    },
    onError: (error_) => {
      setStatus("error");
      updateNotification({
        id: notificationId,
        title: "Erro ao gerar relatório",
        message: error_.message || "Ocorreu um erro ao gerar o relatório",
        color: "red",
        icon: <IconX size={16} />,
        autoClose: NOTIFICATION_DELAY_ERROR,
        loading: false
      });
      setTimeout(() => setStatus("idle"), NOTIFICATION_DELAY_ERROR);
    }
  });

  const handleTemplateSelect = useCallback(
    async (template: TemplateListItem) => {
      if (!data) {
        showNotification({
          title: "Dados insuficientes",
          message: "Aguarde o carregamento dos dados do diagnóstico",
          color: "yellow"
        });
        return;
      }

      setStatus("loading");
      showNotification({
        id: notificationId,
        title: "Gerando relatório",
        message: `Usando template "${template.nome}"...`,
        loading: true,
        autoClose: false
      });

      try {
        await generatePDF(template.id, data);
      } catch {
        // Error handling is done in onError callback
      }
    },
    [data, generatePDF, notificationId]
  );

  const isDisabled = disabled || !data || isGenerating;
  const hasTemplates = templates.length > 0;

  const buttonIcon = getButtonIcon(status);

  const buttonColor = status === "success" ? "success" : status === "error" ? "danger" : undefined;

  const buttonText = isGenerating ? "Gerando..." : "Gerar Relatório";

  const tooltipLabel = getTooltipLabel(data, error, hasTemplates);

  // No templates available - show disabled button
  if (!isLoadingTemplates && !hasTemplates) {
    return (
      <Tooltip label="Nenhum template de relatório configurado">
        <Button
          leftSection={<IconFileOff size={16} />}
          disabled
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          color="gray"
        >
          Sem Templates
        </Button>
      </Tooltip>
    );
  }

  return (
    <Menu shadow="md" width={280} position="bottom-end" disabled={isDisabled}>
      <Menu.Target>
        <Tooltip label={tooltipLabel} disabled={isGenerating}>
          <Button
            leftSection={buttonIcon}
            rightSection={isLoadingTemplates ? <Loader size={14} /> : <IconChevronDown size={14} />}
            loading={isGenerating}
            disabled={isDisabled}
            variant={variant}
            size={size}
            fullWidth={fullWidth}
            color={buttonColor}
            style={{ position: "relative" }}
          >
            {buttonText}
            {isGenerating && progress > 0 && (
              <Progress
                value={progress}
                size="xs"
                color="white"
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  borderRadius: 0
                }}
              />
            )}
          </Button>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>Selecione o template</Menu.Label>
        {isLoadingTemplates ? (
          <Stack align="center" py="md">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Carregando templates...
            </Text>
          </Stack>
        ) : (
          templates.map((template) => (
            <TemplateMenuItem
              key={template.id}
              template={template}
              onSelect={handleTemplateSelect}
              disabled={isGenerating}
            />
          ))
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
